<template>
  <div ref="container" class="methods">
    <div class="section-label methods__label">
      {{ content.headline }}
    </div>

    <div
      v-for="(method, index) in content.items"
      :key="method.title"
      :class="{ 'is-reversed': index % 2 === 1 }"
      class="methods__item"
    >
      <div class="methods__item__title-outer">
        <div class="methods__item__title-inner">
          <img
            ref="pictures"
            :src="`/images/methods/${index}.png`"
            :alt="method.title"
            class="methods__item__picture"
          />
          <h2
            class="h2-custom methods__item__title"
            v-html="splitTitle(method.title)"
          ></h2>
        </div>
      </div>

      <div class="p-medium methods__item__text" v-html="method.text"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { onMounted, ref } from 'vue'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

interface MethodList {
  headline: string
  items: {
    title: string
    text: string
  }[]
}

defineProps({
  content: {
    type: Object as () => MethodList,
    required: true,
  },
})

const isMobile = ref(false)
const container = ref({} as HTMLElement)
const pictures = ref<HTMLElement[]>([])
const initAnimation = () => {
  isMobile.value = window.innerWidth < 768
  const movement = isMobile.value
    ? { y: 30, rotate: 10 }
    : { y: 100, rotate: 20 }
  gsap.fromTo(pictures.value, movement, {
    duration: 1,
    rotate: -movement.rotate,
    y: -movement.y,
    stagger: 0.1,
    ease: 'none',
    scrollTrigger: {
      scrub: 1,
      // markers: true,
      trigger: container.value,
    },
  })
}

const splitTitle = (title: string) => {
  const fragments = title.split('\\')

  return fragments.reduce(
    (prev: string, val: string) => `${prev}<span>${val.trim()}</span>`,
    ''
  )
}

onMounted(() => {
  initAnimation()
})
</script>

<style lang="scss" scoped>
$bp: s;

.methods {
  @include mq(xl) {
    padding: 0 col(1);
  }
}

.methods__label {
  // prettier-ignore
  @include fluid(margin-bottom,(xxs: 70px, xxl: 110px));

  text-align: center;
}

.methods__item {
  &:not(:last-child) {
    // prettier-ignore
    @include fluid(margin-bottom, (xxs: 40px, xxl: 110px));
  }

  @include mq($bp) {
    display: flex;

    &.is-reversed {
      flex-direction: row-reverse;
    }
  }
}

.methods__item__title-outer {
  margin-bottom: 3rem;

  @include mq($bp) {
    flex-shrink: 0;
    width: col(6);
    margin-bottom: 0;
    padding-right: col(0.5);

    .is-reversed & {
      padding-right: 0;
      padding-left: col(0.5);
    }

    .methods__item:last-child & {
      text-align: right;
    }
  }
}

.methods__item__title-inner {
  position: relative;
  display: inline-block;

  @include mq($bp) {
    .methods__item:nth-child(2) & {
      display: flex;
      width: 100%;
    }
  }
}

.methods__item__picture {
  // prettier-ignore
  @include fluid(max-width,(xxs: 80px, xxxl: 200px));

  position: absolute;
  top: 0;
  left: 110%;
  width: 100%;

  .methods__item:last-child & {
    // prettier-ignore
    @include fluid(max-width,(xxs: 100px, xxxl: 180px));
  }

  @include mq($bp) {
    left: 100%;
    transform: translateY(-5rem);

    .methods__item:nth-child(2) & {
      right: 0;
      left: auto;
      transform: translateY(-2rem);
    }

    .methods__item:nth-child(3) & {
      top: 80%;
      left: 0;
      transform: translateY(-2rem);
    }

    .methods__item:last-child & {
      right: 80%;
      left: auto;
    }
  }
}

.methods__item__title {
  display: flex;

  @include mq($bp) {
    flex-direction: column;
    flex-shrink: 0;
    width: 100%;

    .methods__item:last-child & {
      text-align: right;
    }

    :deep(span:nth-child(even)) {
      margin-left: auto;
    }

    :deep(b) {
      display: block;
    }

    :deep(b:last-child) {
      text-align: right;
    }
  }
}

.methods__item__text {
  :deep(:not(p:last-child)) {
    margin-bottom: 3rem;
  }

  @include mq($bp) {
    padding-left: col(0.5);

    .is-reversed & {
      padding-right: col(0.5);
      padding-left: 0;
    }
  }
}
</style>
